import { t } from '@/lib/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { PageContainer } from '@ant-design/pro-components';
import { AsyncBoundary } from '@/components/async-boundary';
import { requireCanManageUsers } from '@/features/auth/session/guards';
import { useConfigs } from '@/features/system-config/system-config-api';
import { ConfigForm } from '@/features/system-config/form';
import { useDocumentTitle } from '@/hooks/use-document-title';

/**
 * System configuration — admin only.
 *
 * The guard uses the same lightweight `GET /auth/capabilities` probe as the user screens, because
 * "may this actor administer this deployment" is one question: the configuration routes are
 * admin-only on the API for the same reason the user routes are. The API decides either way —
 * this only keeps an operator from opening a page that can answer nothing but 403.
 */
export const Route = createFileRoute('/_authenticated/system/configs')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { section?: 'sms' | 'field-visibility' } => {
    if (search.section === 'sms' || search.section === 'field-visibility')
      return { section: search.section };
    return {};
  },
  beforeLoad: async ({ context, location }) => {
    await requireCanManageUsers(context.queryClient, location.href);
  },
  component: ConfigsPage,
});

function ConfigsPage() {
  useDocumentTitle(t('配置管理'));
  const configs = useConfigs();
  const { section } = Route.useSearch();

  return (
    <PageContainer
      header={{
        title: t('配置管理'),
      }}
    >
      <AsyncBoundary result={configs} skeletonRows={8}>
        {(items) => (
          <ConfigForm
            key={section ?? 'config'}
            items={items}
            initialSection={section}
          />
        )}
      </AsyncBoundary>
    </PageContainer>
  );
}
